'use client'
import { useChat, Message as MessageType } from '@ai-sdk/react'
import { generateId } from 'ai'
import React, { useEffect, useRef, useState } from 'react'
import { HiArrowUp } from 'react-icons/hi'
import { RiShareBoxLine } from 'react-icons/ri'
import Textarea from 'react-textarea-autosize'
import { Message } from './message'
import { motion } from 'framer-motion'
import { formatCurrency } from '@/lib/utils'
import { SearchProductsToolResult, VespaPartResult } from '@/lib/utils/vespa'
import { usePostHog } from 'posthog-js/react'
import { questions } from '../lib/constants'
import { useTheme } from '../lib/themeContext' // Import the theme context

export function Chat({
  id,
  savedMessages,
  initialCurrentPrompts,
  initialShowFeedback = false,
  initialWasPromptClicked = false, // This is used for tracking to differentiate between users who clicked on a prompt and those who only use the input field
  readOnly = false
}: {
  readOnly: boolean
  id: string
  savedMessages?: MessageType[]
  initialCurrentPrompts?: typeof questions
  initialShowFeedback?: boolean
  initialWasPromptClicked?: boolean
}) {
  const {
    primary,
    accent,
    secondary,
    questions,
    starterMessages,
    api,
    showCallButton
  } = useTheme() // Consume the theme context
  const posthog = usePostHog()

  const [showInvestorRelationsButtons, setShowInvestorRelationsButtons] =
    useState(false)

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    error,
    reload,
    setMessages,

    status
  } = useChat({
    api: api,
    id,
    // onError: error => {
    //   console.error(error)
    //   console.log(error.message)
    //   console.log(error.cause)
    //   console.log(error.name)
    // },
    initialMessages: savedMessages || starterMessages,
    onToolCall: ({ toolCall }) => {
      if (toolCall.toolName === 'searchInvestorRelations') {
        console.log(toolCall.args)
        setShowInvestorRelationsButtons(true)
      }
    }
  })

  const ref = useRef<HTMLDivElement>(null)

  const [currentPrompts, setCurrentPrompts] = useState<
    { text: string; children?: any; parentText?: string; url?: string }[]
  >(!readOnly ? (initialCurrentPrompts ?? questions) : [])
  const [showFeedback, setShowFeedback] = useState(initialShowFeedback)
  const [wasPromptClicked, setWasPromptClicked] = useState(
    initialWasPromptClicked
  )
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTo({
        top: ref.current.scrollHeight,
        behavior: 'smooth'
      })
    }
    localStorage.setItem(
      'lastChatMessage',
      messages[messages.length - 1]?.content || ''
    )
    localStorage.setItem('lastChatDate', new Date().toISOString())
    localStorage.setItem('currentPrompts', JSON.stringify(currentPrompts))
    localStorage.setItem('showFeedback', JSON.stringify(showFeedback))
    localStorage.setItem('promptClicked', JSON.stringify(wasPromptClicked))
  }, [messages, status])

  function customSubmit(event: React.FormEvent<HTMLFormElement>) {
    if (error != null) {
      setMessages(messages.slice(0, -1)) // remove last message
    }
    setShowFeedback(true)
    setShowInvestorRelationsButtons(false)
    setCurrentPrompts([])
    handleSubmit(event)
  }

  useEffect(() => {
    localStorage.setItem('chatId', id)
  }, [id])

  return (
    <div
      className="relative mx-auto flex size-full h-full max-w-3xl flex-1 flex-col overflow-auto md:px-12"
      style={{ borderColor: primary }}
    >
      <div
        ref={ref}
        className="relative flex h-full flex-1 flex-col gap-2.5 overflow-auto px-3 py-4"
      >
        {/* {messages.map(({ message, direction }, index) => (
          <Message key={index} message={message} direction={direction} />
        ))} */}

        {messages?.map((message, index) => (
          <React.Fragment key={message.id}>
            {message.parts.map(part => {
              switch (part.type) {
                // render text parts as simple text:
                case 'text':
                  return (
                    <Message
                      message={part.text}
                      direction={message.role === 'user' ? 'right' : 'left'}
                    />
                  )

                // for tool invocations, distinguish between the tools and the state:
                case 'tool-invocation': {
                  if (part.toolInvocation.state === 'call') {
                    return <LoadingIndicator />
                  }
                  switch (part.toolInvocation.toolName) {
                    case 'searchProducts': {
                      switch (part.toolInvocation.state) {
                        case 'result':
                          return (
                            <>
                              <Message
                                direction="left"
                                message={`Here are some options available for you:`}
                              />
                              {part.toolInvocation.result.map(
                                (product: SearchProductsToolResult) => {
                                  return (
                                    <ProductCard
                                      key={product.id}
                                      product={product}
                                      onClick={() => {
                                        posthog.capture('$productCardClick', {
                                          $type: 'product',
                                          $product: product.id,
                                          $productUrl: product.productUrl,
                                          $chatId: id,
                                          $make: product.make,
                                          $model: product.model,
                                          $year: product.year,
                                          $stock: product.stockNumber
                                        })
                                      }}
                                    />
                                  )
                                }
                              )}
                            </>
                          )
                      }
                      break
                    }
                    case 'searchParts': {
                      switch (part.toolInvocation.state) {
                        case 'result':
                          return part.toolInvocation.result.map((part: any) => (
                            <PartCard
                              key={part.id}
                              part={part}
                              onClick={() => {
                                posthog.capture('$productCardClick', {
                                  $type: 'part',
                                  $product: part.id,
                                  $productUrl: part.url,
                                  $chatId: id,
                                  $partNumber: part.partNumber
                                })
                              }}
                            />
                          ))
                      }
                      break
                    }
                    case 'careerDevelopment': {
                      switch (part.toolInvocation.state) {
                        case 'result':
                          return (
                            <a
                              target="_blank"
                              className="flex items-center gap-1 self-start rounded-lg bg-[#F3F3F3] p-2.5 text-sm font-semibold underline"
                              key={part.toolInvocation.toolCallId}
                              href={part.toolInvocation.result.link}
                              style={{ color: primary }}
                            >
                              {part.toolInvocation.result.text}
                              <RiShareBoxLine
                                className="h-5 w-5"
                                style={{ color: primary }}
                              />
                            </a>
                          )
                      }
                      break
                    }

                    case 'callbackRequest': {
                      switch (part.toolInvocation.state) {
                        case 'result':
                          return (
                            <>
                              {showCallButton && (
                                <a
                                  href="tel:8882892273"
                                  className="flex items-center justify-center gap-1 rounded-lg p-2 text-sm text-white shadow-md"
                                  style={{ backgroundColor: accent }}
                                >
                                  <span className="font-bold">Call Us:</span>
                                  (888) 289-2273
                                </a>
                              )}
                            </>
                          )
                      }
                      break
                    }
                  }
                }
              }
            })}
          </React.Fragment>
        ))}

        {status === 'ready' &&
          currentPrompts.map((question, index) =>
            question.url ? (
              <a
                target="_blank"
                className="flex items-center gap-1 self-start rounded-lg bg-[#F3F3F3] p-2.5 text-sm font-semibold underline"
                key={question.text}
                href={question.url}
              >
                {question.text}
                <RiShareBoxLine
                  className="h-5 w-5"
                  style={{ color: primary }}
                />
              </a>
            ) : (
              <button
                key={question.text}
                onClick={() => {
                  posthog.capture('$promptClick', {
                    $prompt: question.text,
                    $chatId: id,
                    ...(question?.parentText && {
                      $parentPrompt: question.parentText
                    }) // This is used to identify secondary prompts in reporting
                  })

                  setWasPromptClicked(true)

                  if (question.url) {
                    window.open(question.url, '_blank')
                  } else {
                    setMessages(messages => [
                      ...messages,
                      {
                        content: question.text,
                        id: generateId(),
                        role: 'user'
                      }
                    ])
                    reload()
                    setCurrentPrompts(question.children || [])
                  }
                }}
                className="self-end rounded-3xl border px-2.5 py-2 text-sm font-semibold shadow-md"
                style={{
                  borderColor: primary,
                  color: secondary
                }}
              >
                {question.text}
              </button>
            )
          )}

        {error && (
          <>
            <div style={{ color: primary }}>An error occurred.</div>
            <button
              type="button"
              onClick={() => {
                posthog.capture('retry_click', { $chatId: id })
                return reload()
              }}
              style={{
                borderColor: primary,
                color: secondary
              }}
            >
              Retry
            </button>
          </>
        )}
        {status === 'ready' &&
          showFeedback &&
          !showInvestorRelationsButtons && (
            <div className="flex gap-2.5 self-end">
              {['That Helped', 'Request Callback'].map(text => (
                <button
                  key={text}
                  onClick={() => {
                    posthog.capture('$feedbackClick', {
                      $feedback: text,
                      $chatId: id
                    })
                    setMessages(messages => [
                      ...messages,
                      {
                        content: text,
                        id: generateId(),
                        role: 'user'
                      }
                    ])
                    reload()
                    setShowFeedback(false)
                  }}
                  className="self-end rounded-3xl border border-[#D9D9D9] px-2.5 py-2 text-sm font-semibold shadow-md"
                >
                  {text}
                </button>
              ))}
            </div>
          )}
        {/* Buttons that link to the appropriate investor relations pages */}
        {status === 'ready' && showInvestorRelationsButtons && (
          <div className="flex flex-col gap-2.5 self-end">
            {[
              {
                text: 'Stock Information',
                href: 'https://ir.titanmachinery.com/Stock_Quote'
              },
              {
                text: 'Contact Investor Relations',
                href: 'https://ir.titanmachinery.com/Contact_IR'
              },
              {
                text: 'Events & Presentations',
                href: 'https://ir.titanmachinery.com/Events_Presentations'
              }
            ].map(button => (
              <a
                key={button.text}
                href={button.href}
                target="_blank"
                className="self-end rounded-3xl border border-[#D9D9D9] px-2.5 py-2 text-sm font-semibold shadow-md"
              >
                {button.text}
              </a>
            ))}
          </div>
        )}
        {status === 'submitted' && <LoadingIndicator />}
      </div>
      {!readOnly && (
        <div className="sticky bottom-0 bg-white px-4 pb-12 lg:px-0">
          <form onSubmit={customSubmit}>
            <div className="relative flex w-full items-center">
              <Textarea
                onChange={handleInputChange}
                value={input}
                required
                name="input"
                rows={1}
                tabIndex={0}
                placeholder="Message"
                spellCheck={false}
                className="flex max-h-60 w-full resize-none rounded-3xl border border-input py-4 pl-4 pr-14 text-base leading-5 ring-offset-background placeholder:text-muted-foreground hover:shadow-lg focus-visible:shadow-lg focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                onKeyDown={e => {
                  // Enter should submit the form
                  if (e.key === 'Enter' && !e.shiftKey) {
                    // Prevent the default action to avoid adding a new line
                    e.preventDefault()
                    posthog.capture('$sendClick', {
                      $input: input,
                      $promptClicked: wasPromptClicked ? true : false,
                      $chatId: id
                    })
                    const textarea = e.target as HTMLTextAreaElement
                    textarea.form?.requestSubmit()
                  }
                }}
              />
              <button
                onClick={() => {
                  posthog.capture('$sendClick', {
                    $input: input,
                    $promptClicked: wasPromptClicked ? true : false,
                    $chatId: id
                  })
                }}
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2.5 text-xl"
                style={{ backgroundColor: primary }}
              >
                <HiArrowUp className="text-white" />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

const ProductCard = ({
  product,
  onClick
}: {
  product: SearchProductsToolResult
  onClick: () => void
}) => {
  const { primary } = useTheme() // Consume the theme context
  return (
    <a
      href={product.productUrl}
      target="_blank"
      className="flex flex-col gap-1 rounded-lg bg-[#F3F3F3] p-2.5"
      onClick={onClick}
    >
      <div className="flex gap-1.5">
        <div>
          <img className="w-24" src={product.primaryImage} alt={product.make} />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <a
              href={product.productUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="text-sm font-semibold underline">
                {[product.year, product.make, product.model]
                  .filter(Boolean)
                  .join(' ')}
              </div>
            </a>
            <a
              href={product.productUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <RiShareBoxLine className="h-5 w-5" style={{ color: primary }} />
            </a>
          </div>
          <div className="text-sm font-semibold text-[#4F4F4F]">
            {product.price ? formatCurrency(product.price) : 'Call for Pricing'}
          </div>
          <div className="text-sm font-semibold text-[#4F4F4F]">
            {product.location}
          </div>
        </div>
      </div>
      <div className="line-clamp-2 text-sm text-[#4F4F4F]">
        {product.description}
      </div>
      <div className="text-sm font-semibold text-[#4F4F4F]">
        Stock # {product.dealerStockNumber}
      </div>
    </a>
  )
}

const PartCard = ({
  part,
  onClick
}: {
  part: VespaPartResult
  onClick: () => void
}) => {
  const { primary } = useTheme() // Consume the theme context
  return (
    <a
      target="_blank"
      href={part.url}
      className="flex flex-col gap-1 rounded-lg bg-[#F3F3F3] p-2.5"
      onClick={onClick}
    >
      <div className="flex gap-1.5">
        <div>
          <img className="w-24" src={part.primaryImage} alt={part.title} />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <a href={part.url} target="_blank" rel="noopener noreferrer">
              <div className="text-sm font-semibold underline">
                {part.title}
              </div>
            </a>
            <a href={part.url} target="_blank" rel="noopener noreferrer">
              <RiShareBoxLine className="h-5 w-5" style={{ color: primary }} />
            </a>
          </div>
          {part.price ? (
            <div className="text-sm font-semibold text-[#4F4F4F]">
              {formatCurrency(part.price)}
            </div>
          ) : null}
        </div>
      </div>
      <div className="line-clamp-2 text-sm text-[#4F4F4F]">
        {part.description}
      </div>
      <div className="text-sm font-semibold text-[#4F4F4F]">
        Part # {part.partNumber}
      </div>
    </a>
  )
}

function LoadingIndicator() {
  return <div className="text-xs font-bold text-[#656C79]">Thinking...</div>
}
